import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDate, thaiDateTime } from '../lib/format'

interface PurgeItem {
  document_id: string
  document_name: string
  document_created_at: string
  size_bytes: number
  status: 'planned' | 'erased' | 'held' | 'skipped' | 'failed'
  reason: string
  object_deleted: boolean
  error?: string
  attempted_at?: string
  erased_at?: string
}

interface PurgeRun {
  id: string
  mode: 'dry_run' | 'execute'
  status: 'running' | 'completed' | 'failed'
  documents_days: number
  cutoff: string
  actor_id?: string
  candidates: number
  eligible: number
  purged: number
  held: number
  skipped: number
  failed: number
  bytes_freed: number
  error?: string
  started_at: string
  finished_at?: string
  items?: PurgeItem[]
}

interface RetentionPolicy {
  documents_days: number
  audit_days: number
  auto_purge: boolean
}

const modeLabel: Record<string, string> = {
  dry_run: 'ทดลอง',
  execute: 'ลบจริง',
}

const runStatusLabel: Record<string, string> = {
  running: 'กำลังทำงาน',
  completed: 'เสร็จสิ้น',
  failed: 'ล้มเหลว',
}

const itemStatusLabel: Record<string, string> = {
  planned: 'รอประมวลผล',
  erased: 'ลบแล้ว',
  held: 'อายัด',
  skipped: 'ข้าม',
  failed: 'ล้มเหลว',
}

const reasonLabel: Record<string, string> = {
  eligible: 'เข้าเกณฑ์ลบ',
  legal_hold: 'ถูกอายัดทางกฎหมาย',
  already_erased: 'ลบไปแล้ว',
  within_retention: 'ยังอยู่ในกำหนดเก็บ',
  object_delete_failed: 'ลบไฟล์ไม่สำเร็จ',
  purge_failed: 'ดำเนินการไม่สำเร็จ',
  erased: 'ลบตามกำหนดเก็บ',
}

function bytesLabel(n: number): string {
  if (!n) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

export function Purge() {
  const qc = useQueryClient()
  const [last, setLast] = useState<PurgeRun | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [detailBusy, setDetailBusy] = useState<string | null>(null)

  const policy = useQuery({
    queryKey: ['retention'],
    queryFn: () => api<RetentionPolicy>('/v1/retention'),
    staleTime: 30_000,
  })

  const runs = useQuery({
    queryKey: ['purge-runs'],
    queryFn: () => api<PurgeRun[]>('/v1/purge/runs?limit=20'),
    staleTime: 10_000,
  })

  const preview = useMutation({
    mutationFn: () => apiWrite<PurgeRun>('/v1/purge/preview', 'POST', { limit: 200 }),
    onSuccess: (run) => {
      setLast(run)
      setConfirmOpen(false)
      setConfirmed(false)
      setError(null)
      setMessage(`ตรวจสอบแล้ว ${number(run.candidates)} รายการ · เข้าเกณฑ์ ${number(run.eligible)} · ยังไม่มีไฟล์ถูกลบ`)
      void qc.invalidateQueries({ queryKey: ['purge-runs'] })
    },
    onError: (e) => {
      setMessage(null)
      setError(e instanceof Error ? e.message : 'ตรวจสอบรายการไม่สำเร็จ')
    },
  })

  const execute = useMutation({
    mutationFn: () => apiWrite<PurgeRun>('/v1/purge/execute', 'POST', { execute: true, limit: 200 }),
    onSuccess: (run) => {
      setLast(run)
      setConfirmOpen(false)
      setConfirmed(false)
      setError(null)
      setMessage(
        `ลบแล้ว ${number(run.purged)} รายการ · อายัด ${number(run.held)} · ` +
        `ล้มเหลว ${number(run.failed)} · คืนพื้นที่ ${bytesLabel(run.bytes_freed)}`,
      )
      void qc.invalidateQueries({ queryKey: ['purge-runs'] })
    },
    onError: (e) => {
      setMessage(null)
      setError(e instanceof Error ? e.message : 'ลบข้อมูลไม่สำเร็จ')
    },
  })

  async function openRun(id: string) {
    setDetailBusy(id)
    setError(null)
    try {
      const run = await api<PurgeRun>(`/v1/purge/runs/${encodeURIComponent(id)}`)
      setLast(run)
      setMessage(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'เปิดรายละเอียดรอบไม่สำเร็จ')
    } finally {
      setDetailBusy(null)
    }
  }

  const days = policy.data?.documents_days ?? 0
  const busy = preview.isPending || execute.isPending
  const rows = runs.data ?? []

  return (
    <>
      <PageHead
        title="การลบข้อมูลตามกำหนดเก็บรักษา"
        sub="กวาดเอกสารที่พ้นกำหนดเก็บ · ตรวจสอบรายการก่อนเสมอ · เอกสารที่ถูกอายัดทางกฎหมายจะไม่ถูกแตะต้อง"
      />

      <div className="grid-main">
        <div className="card card-pad">
          <div className="card-label">รอบการกวาด</div>
          <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.75, marginTop: 10 }}>
            ระบบอ่านนโยบายขององค์กรแล้วคัดเอกสารที่สร้างก่อนวันตัดยอด
            การตรวจสอบจะบันทึกรายการที่เข้าเกณฑ์แต่ไม่ลบไฟล์และไม่แก้สถานะเอกสาร
            การลบจริงจะลบไฟล์ต้นฉบับก่อนแล้วจึงทำเครื่องหมายแถวเอกสารว่า erased
          </p>

          <div className="kv" style={{ marginTop: 14 }}>
            <span>กำหนดเก็บเอกสาร</span>
            <span className="tnum">
              {days ? `${number(days)} วัน` : policy.isLoading ? '…' : '—'}
              {days ? <span className="dim"> · ประมาณ {(days / 365).toFixed(1)} ปี</span> : null}
            </span>
          </div>
          <div className="kv">
            <span>กำหนดเก็บร่องรอยตรวจสอบ</span>
            <span className="tnum">
              {policy.data ? `${number(policy.data.audit_days)} วัน` : '—'}
            </span>
          </div>
          <div className="kv">
            <span>กวาดอัตโนมัติ</span>
            <span>{policy.data ? (policy.data.auto_purge ? 'เปิด' : 'ปิด') : '—'}</span>
          </div>

          <div className="row" style={{ gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
            <button
              className="btn btn-ghost"
              type="button"
              disabled={busy}
              onClick={() => preview.mutate()}
            >
              {preview.isPending ? 'กำลังตรวจสอบ…' : 'ตรวจสอบรายการที่จะลบ'}
            </button>
            <button
              className="btn btn-primary"
              type="button"
              disabled={busy}
              onClick={() => {
                setConfirmed(false)
                setConfirmOpen(true)
                setError(null)
              }}
            >
              {execute.isPending ? 'กำลังลบ…' : 'ลบข้อมูลจริง'}
            </button>
          </div>

          {message ? (
            <div className="chip gold" style={{ marginTop: 14, whiteSpace: 'normal', lineHeight: 1.6 }}>
              {message}
            </div>
          ) : null}
          {error ? <div className="form-error" role="alert">{error}</div> : null}

          {confirmOpen ? (
            <div
              className="card card-pad"
              style={{ marginTop: 16, borderColor: 'rgba(166,58,74,0.45)' }}
            >
              <div className="card-label">ยืนยันการลบจริง</div>
              <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.75, marginTop: 10 }}>
                ไฟล์ต้นฉบับของเอกสารที่เข้าเกณฑ์จะถูกลบถาวรและกู้คืนจากระบบไม่ได้
                แถวเอกสารจะยังอยู่เพื่อเป็นหลักฐาน แต่จะไม่มีไฟล์อีก
                เอกสารที่ถูกอายัดทางกฎหมายจะถูกข้ามในรอบนี้
              </p>
              <label className="row" style={{ gap: 8, alignItems: 'flex-start', marginTop: 12, fontSize: 12.5 }}>
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>ข้าพเจ้าเข้าใจว่าไฟล์จะถูกลบถาวร และได้ตรวจสอบรายการล่วงหน้าแล้ว</span>
              </label>
              <div className="row" style={{ gap: 10, marginTop: 14 }}>
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={!confirmed || execute.isPending}
                  onClick={() => execute.mutate()}
                >
                  {execute.isPending ? 'กำลังลบ…' : 'ยืนยันลบข้อมูล'}
                </button>
                <button
                  className="btn btn-ghost"
                  type="button"
                  disabled={execute.isPending}
                  onClick={() => setConfirmOpen(false)}
                >
                  ยกเลิก
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="card card-pad">
          <div className="card-label">หลักประกันของรอบกวาด</div>
          <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.95, margin: '12px 0 0', paddingLeft: 18 }}>
            <li>ค่าเริ่มต้นคือทดลอง: ต้องส่งธง execute อย่างชัดเจนจึงจะลบได้</li>
            <li>legal hold เป็นสิทธิ์เหนือกว่าเวลา เอกสารที่อายัดจะไม่ถูกส่งไปยังที่เก็บไฟล์</li>
            <li>ลบไฟล์ก่อนแล้วจึงทำเครื่องหมายแถว: ถ้าลบไฟล์ไม่สำเร็จ แถวจะคงอยู่ให้รอบถัดไปลองใหม่</li>
            <li>การอ่านสถานะเอกสารซ้ำทำภายใต้ row lock เพื่อไม่ให้ hold ที่เพิ่งถูกตั้งหลุดผ่านไปได้</li>
            <li>ทุกรอบและทุกรายการถูกเก็บไว้เป็นหลักฐาน และแอปไม่มีสิทธิ์ลบทิ้ง</li>
          </ul>
          <p className="dim" style={{ fontSize: 11.5, lineHeight: 1.7, marginTop: 14 }}>
            รอบที่ทดลองจะถูกบันทึกพร้อมรายการทั้งหมด แต่ฐานข้อมูลไม่อนุญาตให้รอบทดลองรายงานยอดลบ
            หรือพื้นที่ที่คืนได้
          </p>
        </div>
      </div>

      {last ? (
        <>
          <div className="row" style={{ gap: 10, marginTop: 24, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="card-label">ผลรอบล่าสุด</span>
            <span className={`chip${last.mode === 'execute' ? ' gold' : ''}`}>
              {modeLabel[last.mode] ?? last.mode}
            </span>
            <span className="chip">{runStatusLabel[last.status] ?? last.status}</span>
            <span className="dim" style={{ fontSize: 11.5 }}>
              เริ่ม {thaiDateTime(last.started_at)} · ตัดยอดก่อน {thaiDate(last.cutoff)}
            </span>
          </div>

          <div className="grid-4" style={{ marginTop: 12 }}>
            <Stat label="ผู้สมัคร" value={number(last.candidates)} foot="เอกสารที่เก่ากว่าวันตัดยอด" />
            <Stat label="ลบแล้ว" value={number(last.purged)} unit="รายการ"
              foot={`คืนพื้นที่ ${bytesLabel(last.bytes_freed)}`} />
            <Stat label="อายัด" value={number(last.held)} foot="legal hold · ไม่ถูกแตะต้อง" />
            <Stat label="ล้มเหลว" value={number(last.failed)}
              foot={last.mode === 'dry_run' ? 'รอบทดลองไม่ลบข้อมูล' : 'แถวเอกสารยังอยู่ให้ลองใหม่'} />
          </div>

          {last.error ? <div className="form-error" role="alert" style={{ marginTop: 12 }}>{last.error}</div> : null}

          <div className="card-label" style={{ marginTop: 24, marginBottom: 10 }}>รายการในรอบ</div>
          {!last.items || last.items.length === 0 ? (
            <div className="card">
              <Empty title="ไม่มีเอกสารเข้าเกณฑ์" body="ยังไม่มีเอกสารที่เก่ากว่ากำหนดเก็บขององค์กรในรอบนี้" />
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>เอกสาร</th>
                    <th>สร้างเมื่อ</th>
                    <th>ขนาด</th>
                    <th>สถานะ</th>
                    <th>เหตุผล</th>
                    <th>ไฟล์</th>
                  </tr>
                </thead>
                <tbody>
                  {last.items.map((item) => (
                    <tr key={item.document_id} style={{ cursor: 'default' }}>
                      <td>
                        <div>{item.document_name || '—'}</div>
                        <div className="dim mono" style={{ fontSize: 10.5 }}>{item.document_id}</div>
                      </td>
                      <td className="dim">{thaiDate(item.document_created_at)}</td>
                      <td className="dim tnum">{bytesLabel(item.size_bytes)}</td>
                      <td>
                        <span className={`chip${item.status === 'erased' ? ' gold' : ''}`}>
                          {itemStatusLabel[item.status] ?? item.status}
                        </span>
                      </td>
                      <td>
                        <div>{reasonLabel[item.reason] ?? item.reason}</div>
                        {item.error ? (
                          <div className="dim" style={{ fontSize: 10.5, marginTop: 2 }}>{item.error}</div>
                        ) : null}
                      </td>
                      <td className="dim">
                        {item.object_deleted ? 'ลบแล้ว' : item.status === 'held' ? 'เก็บไว้' : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {last.mode === 'dry_run' ? (
            <p className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
              รอบนี้เป็นการทดลอง: ไฟล์ทั้งหมดยังอยู่ และไม่มีแถวเอกสารใดถูกเปลี่ยนสถานะ
            </p>
          ) : null}
        </>
      ) : null}

      <div className="card-label" style={{ marginTop: 28, marginBottom: 10 }}>ประวัติรอบกวาด</div>
      {runs.isError ? (
        <div className="card"><div className="form-error" role="alert" style={{ margin: 16 }}>โหลดประวัติรอบกวาดไม่สำเร็จ</div></div>
      ) : runs.isLoading ? (
        <div className="stack"><Skel h={44} /><Skel h={44} /><Skel h={44} /></div>
      ) : rows.length === 0 ? (
        <div className="card">
          <Empty title="ยังไม่มีรอบกวาด" body="กดตรวจสอบรายการเพื่อสร้างรอบทดลองรอบแรก ระบบจะบันทึกไว้ให้ตรวจย้อนหลัง" />
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>เริ่ม</th>
                <th>โหมด</th>
                <th>ผู้สมัคร</th>
                <th>ลบแล้ว</th>
                <th>อายัด</th>
                <th>ล้มเหลว</th>
                <th>สถานะ</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((run) => (
                <tr key={run.id} style={{ cursor: 'default' }}>
                  <td>
                    <div>{thaiDateTime(run.started_at)}</div>
                    <div className="dim" style={{ fontSize: 10.5 }}>
                      {run.finished_at ? `จบ ${thaiDateTime(run.finished_at)}` : 'ยังไม่จบ'}
                    </div>
                  </td>
                  <td>
                    <span className={`chip${run.mode === 'execute' ? ' gold' : ''}`}>
                      {modeLabel[run.mode] ?? run.mode}
                    </span>
                  </td>
                  <td className="tnum">{number(run.candidates)}</td>
                  <td className="tnum">{number(run.purged)}</td>
                  <td className="tnum">{number(run.held)}</td>
                  <td className="tnum">{number(run.failed)}</td>
                  <td>{runStatusLabel[run.status] ?? run.status}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-ghost"
                      type="button"
                      disabled={detailBusy === run.id}
                      onClick={() => void openRun(run.id)}
                    >
                      {detailBusy === run.id ? 'กำลังเปิด…' : 'ดูรายละเอียด'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
